import * as m from '@staiter/i18n';
import type { AuditEventLegacy as AuditEvent } from '@staiter/shared';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { ExportMenu, ListPageShell, PageHeader, Table } from '~/components/data';
import { Button, DateRangePicker, Drawer, Input } from '~/components/primitives';
import { type AuditFilter, fetchAudit } from '~/lib/api/admin';
import { useListPageState } from '~/lib/hooks/useListPageState';
import shared from './_auth._shared.module.css';
import styles from './_auth.admin.module.css';

export const Route = createFileRoute('/_auth/admin/audit')({
  staticData: { minMainWidth: 960 },
  component: AuditAdmin,
});

function fmt(ts: string): string {
  try {
    return new Date(ts).toLocaleString();
  } catch {
    return ts;
  }
}

// Simple PII masker — emails/phones из payload-строк маскируем последние символы.
function maskPii(value: unknown): unknown {
  if (typeof value === 'string') {
    return value
      .replace(/([\w.+-]{1,3})[\w.+-]*@([\w-]+\.[\w.-]+)/g, '$1***@$2')
      .replace(/(\+?\d{1,3})\d{6,9}(\d{2})/g, '$1******$2');
  }
  if (Array.isArray(value)) return value.map(maskPii);
  if (value && typeof value === 'object') {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) out[k] = maskPii(v);
    return out;
  }
  return value;
}

function AuditAdmin(): JSX.Element {
  // FE-274 Pass 25: client-side category filter (read / write / approve).
  // Compliance officer тонул в read-only navigation noise — default = «все».
  const [filter, setFilter] = useState<AuditFilter>({});
  const [actionCategory, setActionCategory] = useState<'all' | 'read' | 'write' | 'approve'>('all');
  const [selected, setSelected] = useState<AuditEvent | null>(null);

  const q = useQuery({
    queryKey: ['admin', 'audit', filter],
    queryFn: () => fetchAudit(filter),
  });
  const state = useListPageState(q);

  const onChange =
    (field: keyof AuditFilter) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>): void => {
      const v = e.target.value;
      setFilter((f) => ({ ...f, [field]: v || undefined }));
    };

  return (
    <div className={shared.page}>
      <PageHeader
        title={m.route_admin_audit_title()}
        description={m.route_admin_audit_description()}
        actions={
          <ExportMenu
            resource="admin/audit"
            filters={filter as Record<string, string | undefined>}
          />
        }
      />

      <div className={shared.filters}>
        <Input
          label={m.route_admin_audit_filter_actor()}
          value={filter.actor ?? ''}
          onChange={onChange('actor')}
        />
        <Input
          label={m.route_admin_audit_filter_resource()}
          value={filter.resource ?? ''}
          onChange={onChange('resource')}
        />
        <Input
          label={m.route_admin_audit_filter_action()}
          value={filter.action ?? ''}
          onChange={onChange('action')}
        />
        <Input
          label={m.route_admin_audit_filter_entity_type()}
          value={filter.entity_type ?? ''}
          onChange={onChange('entity_type')}
        />
        <Input
          label={m.route_admin_audit_filter_entity_id()}
          value={filter.entity_id ?? ''}
          onChange={onChange('entity_id')}
        />
        <DateRangePicker
          label={m.route_admin_audit_filter_period()}
          value={{ from: filter.date_from ?? null, to: filter.date_to ?? null }}
          onChange={(r) =>
            setFilter((f) => ({
              ...f,
              date_from: r.from ?? undefined,
              date_to: r.to ?? undefined,
            }))
          }
        />
      </div>

      {/* FE-274 Pass 25: category filter chips — read / write / approve / all. */}
      <div className={shared.filters}>
        {(['all', 'read', 'write', 'approve'] as const).map((cat) => (
          <button
            key={cat}
            type="button"
            onClick={() => setActionCategory(cat)}
            aria-pressed={actionCategory === cat}
            style={{
              padding: '4px 12px',
              borderRadius: 'var(--radius-pill)',
              border: '1px solid var(--color-border)',
              background: actionCategory === cat ? 'var(--color-accent-bg-15)' : 'transparent',
              color:
                actionCategory === cat
                  ? 'var(--color-text-primary)'
                  : 'var(--color-text-secondary)',
              fontSize: 13,
              cursor: 'pointer',
            }}
          >
            {cat === 'all'
              ? m.route_admin_audit_filter_cat_all()
              : cat === 'read'
                ? m.route_admin_audit_filter_cat_read()
                : cat === 'write'
                  ? m.route_admin_audit_filter_cat_write()
                  : m.route_admin_audit_filter_cat_approve()}
          </button>
        ))}
      </div>

      <ListPageShell
        state={state}
        emptyTitle={m.route_admin_audit_empty_title()}
        emptyDescription={m.route_admin_audit_empty_desc()}
      >
        {(items) => (
          <Table
            ariaLabel={m.route_admin_audit_title()}
            data={items.filter((e) => {
              if (actionCategory === 'all') return true;
              if (actionCategory === 'read') return e.action.startsWith('read:');
              if (actionCategory === 'write') return e.action.startsWith('write:');
              if (actionCategory === 'approve')
                return e.action.startsWith('approve:') || e.action.startsWith('push:');
              return true;
            })}
            rowKey={(e) => e.id}
            columns={[
              {
                id: 'ts',
                header: m.route_admin_audit_col_time(),
                accessor: (e) => <span className={styles.mono}>{fmt(e.occurred_at)}</span>,
              },
              { id: 'actor', header: m.route_admin_audit_filter_actor(), accessor: (e) => e.actor },
              {
                id: 'action',
                header: m.route_admin_audit_filter_action(),
                // FE-258 Pass 33: split verb prefix from resource path для читаемости.
                // Verb получает severity-badge, body — mono path с › разделителями.
                accessor: (e) => <ActionCell action={e.action} />,
              },
              {
                id: 'res',
                header: m.route_admin_audit_filter_resource(),
                accessor: (e) => e.resource,
              },
              {
                id: 'entity',
                header: m.route_admin_audit_col_entity(),
                accessor: (e) => (e.entity_type ? `${e.entity_type}/${e.entity_id ?? '—'}` : '—'),
              },
              {
                id: 'status',
                header: m.route_admin_audit_col_status(),
                accessor: (e) =>
                  e.status === 'ok' ? (
                    <span className={styles.validBadge}>{m.route_admin_audit_status_ok()}</span>
                  ) : (
                    <span className={styles.invalidBadge}>
                      {m.route_admin_audit_status_error()}
                    </span>
                  ),
              },
              {
                id: 'open',
                header: '',
                accessor: (e) => (
                  <Button size="sm" variant="ghost" onClick={() => setSelected(e as AuditEvent)}>
                    {m.route_admin_audit_btn_details()}
                  </Button>
                ),
              },
            ]}
          />
        )}
      </ListPageShell>

      <Drawer
        open={!!selected}
        onClose={() => setSelected(null)}
        title={selected ? `${selected.action} — ${selected.resource}` : ''}
        width={560}
      >
        {selected && (
          <div>
            <dl className={styles.kvList}>
              <dt>{m.route_admin_audit_col_time()}</dt>
              <dd className={styles.mono}>{fmt(selected.occurred_at)}</dd>
              <dt>{m.route_admin_audit_filter_actor()}</dt>
              <dd>{selected.actor}</dd>
              <dt>{m.route_admin_audit_drawer_trace_id()}</dt>
              <dd className={styles.mono}>{selected.trace_id ?? '—'}</dd>
              <dt>{m.route_admin_audit_drawer_latency()}</dt>
              <dd className={styles.mono}>
                {selected.latency_ms !== null && selected.latency_ms !== undefined
                  ? m.route_admin_audit_drawer_latency_ms({ value: selected.latency_ms })
                  : '—'}
              </dd>
              <dt>{m.route_admin_audit_drawer_input_hash()}</dt>
              <dd className={styles.mono}>{selected.input_hash ?? '—'}</dd>
              <dt>{m.route_admin_audit_drawer_output_hash()}</dt>
              <dd className={styles.mono}>{selected.output_hash ?? '—'}</dd>
            </dl>
            <h3 className={[shared.sectionHeading, styles.sectionGap].join(' ')}>
              {m.route_admin_audit_drawer_payload_heading()}
            </h3>
            <pre className={styles.payloadJson}>
              {JSON.stringify(maskPii(selected.payload), null, 2)}
            </pre>
          </div>
        )}
      </Drawer>
    </div>
  );
}

// FE-258 Pass 33: ActionCell — verb prefix + resource path with › separator.
const VERB_LABELS: Record<string, () => string> = {
  read: m.route_admin_audit_filter_cat_read,
  write: m.route_admin_audit_filter_cat_write,
  approve: m.route_admin_audit_filter_cat_approve,
  push: m.route_admin_audit_filter_cat_approve,
};
function ActionCell({ action }: { action: string }): JSX.Element {
  const idx = action.indexOf(':');
  if (idx === -1) return <code>{action}</code>;
  const verb = action.slice(0, idx);
  const path = action
    .slice(idx + 1)
    .replace(/\./g, ' › ')
    .replace(/_/g, ' ');
  const verbLabel = (VERB_LABELS[verb] ?? (() => verb))();
  return (
    <span style={{ display: 'inline-flex', gap: 8, alignItems: 'baseline' }}>
      <span
        data-verb={verb}
        style={{
          fontSize: 11,
          padding: '1px 6px',
          borderRadius: 4,
          background:
            verb === 'write'
              ? 'var(--color-info-bg-15, var(--color-accent-bg-15))'
              : verb === 'approve' || verb === 'push'
                ? 'var(--color-ok-bg-15)'
                : 'var(--color-bg-alt)',
          color: 'var(--color-text-secondary)',
        }}
      >
        {verbLabel}
      </span>
      <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12 }}>{path}</span>
    </span>
  );
}
